import React, { useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { Activity, RefreshCw, AlertCircle, History, Lock, Disc, Briefcase, Info, User, LogOut, Crosshair, FlaskConical, BarChart2, ExternalLink } from 'lucide-react';
import { AuthProvider, AuthContext } from './contexts/AuthContext';
import { LinksIDService } from './services/api';
import { auth as firebaseAuth } from './services/firebase';
import { SimulatorProvider } from './contexts/SimulatorContext';
import { GolfSwingIcon } from './components/icons/GolfSwingIcon';
import AuthModal from './components/AuthModal';
import { SessionExpiredBanner } from './components/SessionExpiredBanner';

// Pages
import LandingPage from './pages/LandingPage';
import FitterPage from './pages/FitterPage';
import FitterNoAnalysis from './pages/FitterNoAnalysis';
import BagPage from './pages/BagPage';
import HistoryPage from './pages/HistoryPage';
import InventoryPage from './pages/InventoryPage';
import AdminPage from './pages/AdminPage';
import AboutPage from './pages/AboutPage';
import SimpleUploadPage from './pages/SimpleUploadPage';
import SessionAnalyticsPage from './pages/SessionAnalyticsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background text-text-primary flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-surface border border-status-error/50 rounded-lg p-6 shadow-xl">
            <h2 className="text-xl font-heading font-bold text-status-error mb-4 flex items-center gap-2">
              <AlertCircle /> Something went wrong
            </h2>
            <div className="text-sm text-text-secondary mb-4">
              The application encountered an unexpected error.
            </div>
            {this.state.error && (
              <div className="bg-background p-3 rounded text-xs font-mono text-status-error overflow-auto max-h-48 mb-4">
                {this.state.error.toString()}
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-surface-light hover:bg-border text-text-primary py-2 rounded-lg font-semibold transition-colors"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wrapper for FitterPage to handle location state from HistoryPage navigation
// and LinksID session import via ?linksid_session= URL param
const FitterPageWrapper = () => {
  const location = useLocation();
  const [prefillData, setPrefillData] = useState(location.state?.prefillData || null);
  const [linksidUpload, setLinksidUpload] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clubExport = params.get('linksid_club_export');
    const linksidSession = params.get('linksid_session');
    if (linksidUpload || prefillData) return;

    let exportUrl = null;
    if (clubExport) {
      exportUrl = `https://linksid.colewinds-dev.com/sessions/export-club?${clubExport}`;
    } else if (linksidSession) {
      exportUrl = `https://linksid.colewinds-dev.com/sessions/${linksidSession}/export`;
    }

    if (exportUrl) {
      fetch(exportUrl)
        .then(res => {
          if (!res.ok) throw new Error('Failed to load LinksID data');
          return res.json();
        })
        .then(data => {
          const sess = data.session || {};
          const rawShots = data.shots || [];

          // Format shots to match PocketFitter's CSV upload response structure
          const shots = rawShots.map((s, i) => ({
            id: i + 1,
            shot_number: s.shot_number || i + 1,
            ball_speed: s.ball_speed,
            club_speed: s.club_speed,
            launch_angle: s.launch_angle,
            spin_rate: s.spin_rate,
            spin_axis: s.spin_axis,
            attack_angle: s.attack_angle,
            carry: s.carry_distance,
            total: s.total_distance,
            offline: s.offline,
            smash_factor: s.smash_factor,
            club_path: s.club_path,
            face_angle: s.face_angle,
            face_to_path: s.face_to_path,
            dynamic_loft: s.dynamic_loft,
            max_height: s.apex,
            descent_angle: s.descent,
            flight_time: s.flight_time,
          }));

          // Compute aggregated averages
          const avg = (key) => {
            const vals = shots.map(s => s[key]).filter(v => v != null);
            return vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : null;
          };

          const aggregated = {
            ball_speed: avg('ball_speed'),
            club_speed: avg('club_speed'),
            launch_angle: avg('launch_angle'),
            spin_rate: avg('spin_rate'),
            spin_axis: avg('spin_axis'),
            attack_angle: avg('attack_angle'),
            carry: avg('carry'),
            total: avg('total'),
            smash_factor: avg('smash_factor'),
          };

          // Map club_type for fit type selection
          const clubType = sess.club_type || 'Driver';

          setLinksidUpload({
            status: 'success',
            shots,
            aggregated,
            shot_count: shots.length,
            best_shot: shots[0] || null,
            linksid_club_type: clubType,
          });
        })
        .catch(err => console.error('LinksID import failed:', err));
    }
  }, [location.search]);

  return <FitterPage prefillData={prefillData} linksidUpload={linksidUpload} />;
};

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SimulatorProvider>
          <BrowserRouter>
            <MainLayout />
          </BrowserRouter>
        </SimulatorProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function MainLayout() {
  const { user, passport, loading, login, logout } = useContext(AuthContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const handleSessionExpired = useCallback(() => setSessionExpired(true), []);
  useEffect(() => {
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [handleSessionExpired]);

  const navLinkClass = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium rounded-md flex gap-1.5 items-center transition-colors ${
      isActive ? 'bg-surface-light text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-surface-light/50'
    }`;

  const aboutLinkClass = ({ isActive }) =>
    `px-3 py-2 text-sm font-medium rounded-md flex gap-1.5 items-center transition-colors ${
      isActive ? 'bg-primary text-primary-foreground font-bold' : 'text-primary hover:text-primary-light hover:bg-primary/10'
    }`;

  return (
    <div className="min-h-screen bg-background text-text-primary font-sans selection:bg-primary/30">
      <SessionExpiredBanner
        visible={sessionExpired}
        onSignIn={() => { setSessionExpired(false); setShowAuthModal(true); }}
        onDismiss={() => setSessionExpired(false)}
      />
      <header className="border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <NavLink to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-dark to-primary-light rounded flex items-center justify-center shadow-md">
              <GolfSwingIcon size={20} color="white" />
            </div>
            <h1 className="font-heading font-bold text-lg tracking-tight text-text-primary">
              PocketFitter
            </h1>
          </NavLink>
          
          <nav className="flex gap-1 flex-wrap">
            <NavLink to="/fitter" className={navLinkClass} title="Get equipment recommendations based on your swing data"><Crosshair size={16} className="md:w-3.5 w-5"/><span className="hidden sm:inline"> Fitter</span></NavLink>
            <NavLink to="/analytics" className={navLinkClass} title="Analyze and visualize your practice sessions"><BarChart2 size={16} className="md:w-3.5 w-5"/><span className="hidden sm:inline"> Analytics</span></NavLink>
            <NavLink to="/inventory" className={navLinkClass} title="Browse available equipment"><Disc size={16} className="md:w-3.5 w-5"/><span className="hidden sm:inline"> Inventory</span></NavLink>
            <NavLink to="/bag" className={navLinkClass} title="Manage your golf bag setup"><Briefcase size={16} className="md:w-3.5 w-5"/><span className="hidden sm:inline"> Bag</span></NavLink>
            <NavLink to="/history" className={navLinkClass} title="View your saved builds and session history"><History size={16} className="md:w-3.5 w-5"/><span className="hidden sm:inline"> History</span></NavLink>
            <NavLink to="/admin" className={navLinkClass} title="Admin panel"><Lock size={16} className="md:w-3.5 w-5"/><span className="hidden sm:inline"> Admin</span></NavLink>
            <NavLink to="/about" className={aboutLinkClass} title="About PocketFitter"><Info size={16} className="md:w-3.5 w-5"/><span className="hidden sm:inline"> About</span></NavLink>
          </nav>

          <div>
            {loading ? (
              <RefreshCw className="animate-spin text-text-muted" size={16} />
            ) : user?.isGuest ? (
              <button
                onClick={() => setShowAuthModal(true)}
                className="text-sm text-text-muted flex items-center gap-2 hover:text-text-primary transition-colors"
              >
                <User size={14}/> Guest Mode · Sign In
              </button>
            ) : user ? (
              <div className="flex items-center gap-3">
                {passport?.handle ? (
                  <button
                    onClick={async () => {
                      try {
                        if (passport.profile_visibility !== 'public' && firebaseAuth?.currentUser) {
                          const token = await firebaseAuth.currentUser.getIdToken();
                          await LinksIDService.updatePassport(token, { profile_visibility: 'public' });
                        }
                      } catch (e) { /* best effort */ }
                      window.open(`https://linksid.colewinds-dev.com/profile/${passport.handle}`, '_blank');
                    }}
                    className="text-sm font-medium hidden md:flex items-center gap-1.5 hover:opacity-80 transition-opacity"
                    title="View LinksID Profile"
                  >
                    <span className="text-emerald-400">@{passport.handle}</span>
                    <ExternalLink size={12} className="text-emerald-400/60" />
                  </button>
                ) : (
                  <span className="text-sm text-text-primary font-medium hidden md:block">{user.email || user.displayName}</span>
                )}
                <button onClick={logout} className="p-2 hover:bg-surface-light rounded-full transition-colors text-text-secondary hover:text-text-primary" title="Sign Out"><LogOut size={16} /></button>
              </div>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className="flex items-center gap-2 bg-surface hover:bg-surface-light px-4 py-2 rounded-lg text-sm transition-colors font-medium border border-border"
              >
                <User size={14}/> Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 pb-20">
        <Routes>
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="/fitter" element={<FitterPageWrapper />} />
          <Route path="/analytics" element={<SessionAnalyticsPage />} />
          <Route path="/simple" element={<SimpleUploadPage />} />
          <Route path="/fitter-test" element={<FitterNoAnalysis />} />
          <Route path="/bag" element={<BagPage />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/about" element={<AboutPage />} />
        </Routes>
      </main>

      {/* Version Footer */}
      <footer className="fixed bottom-0 left-0 right-0 border-t border-border bg-background/95 backdrop-blur-md shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between text-xs text-text-muted">
          <div className="flex items-center gap-3">
            <span className="font-medium">PocketFitter v34.4</span>
            <span className="text-text-secondary">•</span>
            <span>Build 2026-02-03</span>
          </div>
          <div className="flex gap-4 items-center">
            <a
              href="https://github.com/yourusername/pocketfitter/blob/main/CHANGELOG.md"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors underline"
            >
              Changelog
            </a>
            <span className="text-primary font-semibold">✓ Production Ready</span>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </div>
  );
}
